import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { debounce, isNull, uniq } from 'lodash';
import moment from 'moment-timezone';
import { useHistory } from 'react-router-dom';
import { Waypoint } from 'react-waypoint';

import LoadingIndicator from '@magoosh/layouts/components/loading_indicator';
import { PrimaryButton } from '@magoosh/lib/button';
import Dropdown from '@magoosh/lib/dropdown';
import NoValueDash from '@magoosh/organisms/no_value_dash';
import { paths } from 'config/path_helpers';
import { PracticeSession } from 'types/practice_session';
import { pluralize } from 'utilities/pluralize';

import ContextualLink from '@test_prep/app/components/contextual_link';
import * as api from '@test_prep/app/api';

import style from './style.module.scss';

interface Props {
  practiceSessions: PracticeSession[]
  onChange: (practiceSessions: PracticeSession[]) => void;
  studentId?: number;
}

export default (props: Props) => {
  const history = useHistory();
  const [page, setPage] = useState<number>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState(false);
  const restoredFromHistory = useRef(false);

  const navigateToPracticeSession = (e: React.MouseEvent, practiceSessionId: number) => {
    const url = paths.reviewAnswers({ filters: { practice_session_id: practiceSessionId } }, props.studentId);
    if (e.metaKey || e.ctrlKey) {
      window.open(url, '_blank');
    } else {
      history.push(url);
    }
  };

  const saveToHistoryState = (practiceSessions, practiceSessionsPage) => {
    window.history.replaceState({ practiceSessions, practiceSessionsPage, scrollY: window.scrollY }, '');
  };

  const loadPracticeSessions = (pageToFetch, clearPracticeSessions = false) => {
    setIsLoading(true);
    if (clearPracticeSessions) props.onChange([])

    api.fetchPracticeSessions(pageToFetch, props.studentId).then((loadedPracticeSessions) => {
      const newPage = loadedPracticeSessions.length === 25 ? pageToFetch : null;
      setPage(newPage);

      const updatedPracticeSessions = clearPracticeSessions
        ? loadedPracticeSessions
        : [...props.practiceSessions, ...loadedPracticeSessions];

      props.onChange(updatedPracticeSessions);
      saveToHistoryState(updatedPracticeSessions, newPage);
      setIsLoading(false);
    });
  };

  const practiceSessionPerformance = (practiceSession) => {
    if (practiceSession.performance.length > 1) return 'Mixed';

    const performance = practiceSession.performance[0];
    if (isNull(performance.value)) return null;

    if (performance.percentage) {
      return `${performance.value}%`;
    } else {
      return `${performance.value} out of ${performance.range.high}`;
    }
  };

  useEffect(() => {
    const historyState = window.history.state as any;
    const isBackForwardNavigation = history.action === 'POP';

    if (!restoredFromHistory.current && isBackForwardNavigation && historyState?.practiceSessions) {
      restoredFromHistory.current = true;
      props.onChange(historyState.practiceSessions);
      setPage(historyState.practiceSessionsPage);
      requestAnimationFrame(() => window.scrollTo(0, historyState.scrollY || 0));
    } else {
      restoredFromHistory.current = true;
      window.history.replaceState(null, '');
      loadPracticeSessions(1, true);
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    const handleScroll = debounce(() => {
      if (window.history.state?.practiceSessions) {
        window.history.replaceState({ ...window.history.state, scrollY: window.scrollY }, '');
      }
    }, 100);

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles';

  if (isMobile) {
    // TODO - make entire cell clickable
    return (
      <div className="d-flex flex-column gap-s" style={{ padding: '20px 15px' }}>
        {
          props.practiceSessions.length > 0 || isLoading ? (
            <>
              {
                props.practiceSessions.map((practiceSession, idx) => {
                  let sectionAndSubjects = uniq([
                    practiceSession.examSection.name,
                    ...practiceSession.subjectTags
                  ]);

                  if (sectionAndSubjects.length > 3) {
                    sectionAndSubjects = [
                      ...sectionAndSubjects.slice(0, 3),
                      `and ${sectionAndSubjects.length - 3} more`
                    ];
                  }

                  return (
                    <React.Fragment key={practiceSession.id}>
                      {
                        idx === props.practiceSessions.length - 20 && !isLoading && page && (
                          <Waypoint onEnter={() => loadPracticeSessions(page + 1)} />
                        )
                      }

                      <div
                        className="d-flex flex-column gap-s"
                        onClick={(e) => navigateToPracticeSession(e, practiceSession.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="align-items-center d-flex flex-1 gap-s">
                          <div className="flex-1" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            <strong>{practiceSession.name}</strong>
                          </div>

                          <div className="small">
                            {moment(practiceSession.createdAt * 1000).tz(timeZone).format('MMM D')}
                          </div>
                        </div>

                        <table className={cx(style.mobileTable, 'small')}>
                          {
                            practiceSession.nameDetail && (
                              <tr>
                                <td style={{ width: 120 }}>Test</td>
                                <td>{practiceSession.nameDetail}</td>
                              </tr>
                            )
                          }

                          <tr>
                            <td style={{ width: 120 }}>Section/Subject</td>
                            <td>{sectionAndSubjects.join(', ')}</td>
                          </tr>

                          <tr>
                            <td style={{ width: 120 }}>Performance</td>
                            <td>
                              {
                                !isNull(practiceSessionPerformance(practiceSession)) && (
                                  <>
                                    {practiceSessionPerformance(practiceSession)}
                                    {', '}
                                  </>
                                )
                              }
                              {pluralize(practiceSession.promptCount, 'question')}
                            </td>
                          </tr>
                        </table>
                      </div>

                      {
                        idx !== props.practiceSessions.length - 1 && (
                          <hr className="u-margin-V-xxs" />
                        )
                      }
                    </React.Fragment>
                  );
                })
              }

              {
                isLoading && (
                  <div className="d-flex justify-content-center">
                    <LoadingIndicator />
                  </div>
                )
              }

              {
                !isLoading && page && (
                  <div className="d-flex justify-content-center">
                    <PrimaryButton onClick={() => loadPracticeSessions(page + 1)}>
                      Show More Practice Sessions
                    </PrimaryButton>
                  </div>
                )
              }
            </>
          ) : (
            <div className="align-items-center d-flex flex-1 flex-column gap-s u-margin-V-m">
              No practice sessions found.
            </div>
          )
        }
      </div>
    );
  } else {
    return (
      <table className={style.table}>
        <thead>
          <th>Session</th>
          <th className={style.colSection}>Section</th>
          <th className={style.colSubject}>Subject</th>
          <th className={cx(style.colQuestions, style.noWrap, 'text-center')}>Questions</th>
          <th className={cx(style.colPerformance, style.noWrap, 'text-center visible-lg')}>Performance</th>
          <th className={cx(style.colDate, style.noWrap, 'text-center')}>Date</th>
        </thead>

        <tbody>
          {
            props.practiceSessions.length > 0 || isLoading ? (
              <>
                {
                  props.practiceSessions.map((practiceSession, idx) => {
                    let subjectTags = practiceSession.subjectTags;

                    if (subjectTags.length > 3) {
                      subjectTags = [
                        ...subjectTags.slice(0, 3),
                        `and ${subjectTags.length - 3} more`
                      ];
                    }

                    return (
                    <React.Fragment key={practiceSession.id}>
                      <tr
                        className={style.clickableRow}
                        onClick={(e) => navigateToPracticeSession(e, practiceSession.id)}
                      >
                        <td>
                          {
                            idx === props.practiceSessions.length - 20 && !isLoading && page && (
                              <Waypoint onEnter={() => loadPracticeSessions(page + 1)} />
                            )
                          }

                          <div className="d-flex flex-column">
                            <div>
                              <strong>{practiceSession.name}</strong>
                            </div>

                            <div className="small text-gray-light">
                              {practiceSession.nameDetail}
                            </div>
                          </div>
                        </td>

                        <td>
                          {practiceSession.examSection.name || <NoValueDash />}
                        </td>

                        <td>
                          {
                            subjectTags.length > 0 ? (
                              subjectTags.join(', ')
                            ) : <NoValueDash />
                          }
                        </td>

                        <td className={cx(style.noWrap, 'text-center')}>
                          {practiceSession.promptCount}
                        </td>

                        <td className={cx(style.noWrap, 'text-center visible-lg')}>
                          {practiceSessionPerformance(practiceSession) || <NoValueDash />}
                        </td>

                        <td className={cx(style.noWrap, 'text-center')}>
                          {moment(practiceSession.createdAt * 1000).tz(timeZone).format('MMM D')}
                        </td>
                      </tr>
                    </React.Fragment>
                    );
                  })
                }

                {
                  isLoading && (
                    <tr>
                      <td colSpan={9}>
                        <div className="d-flex justify-content-center">
                          <LoadingIndicator />
                        </div>
                      </td>
                    </tr>
                  )
                }

                {
                  !isLoading && page && (
                    <tr>
                      <td colSpan={9}>
                        <div className="d-flex justify-content-center">
                          <PrimaryButton onClick={() => loadPracticeSessions(page + 1)}>
                            Show More Practice Sessions
                          </PrimaryButton>
                        </div>
                      </td>
                    </tr>
                  )
                }
              </>
            ) : (
              <tr >
                <td colSpan={9}>
                  <div className="align-items-center d-flex flex-1 flex-column gap-s u-margin-V-m">
                    No practice sessions found.
                  </div>
                </td>
              </tr>
            )
          }
        </tbody>
      </table>
    );
  }
};
